/**
 * SwitchSettingsDialog — switch-WIDE settings, and the pieces every switch write panel shares.
 *
 * WHY THIS EXISTS (owner, 2026-09-14)
 * "I should be able to do that myself, port configuration should be fully functional from the
 * GUI." The trigger was turning global 802.1X off on the ECS4150-28P and saving it -- which took
 * an SSH session. This dialog is that job: 802.1X on/off for the whole switch, and an explicit
 * "Save to startup-config", both reachable from the device sheet and the Switches page.
 *
 * SAFETY MODEL (same as the port panels)
 *   * Propose-then-confirm: Preview renders the exact ecCLI with no device contact and shows the
 *     ACCESS PATH (direct, or via which gateway) Apply will use. Apply sends exactly what was
 *     previewed; changing any input after Preview invalidates it (no stale-preview applies).
 *   * The consequence is stated in plain words ON the control, before the choice is applied --
 *     both 802.1X directions can hurt, in different ways.
 *   * Results are honest: "saved" only when the switch confirmed the flash write
 *     (lib/switchApplyResult). Anything short of that offers "Save to startup-config".
 *
 * DESIGN SYSTEM: surface / surface-2 / line / ink tokens; the status ramp (warn / critical /
 * healthy) only for operational consequence and outcome; brand only for interactive affordances
 * (selected option, primary action). Tablet-first: 44px targets, one column, scrolls in-dialog.
 */
import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import {
  useSwitchDot1x,
  useSwitchSave,
  type PortConfigResponse,
  type SwitchDot1xResponse,
} from "@/lib/api/hooks";
import {
  describeAccess,
  describeApplyOutcome,
  describeSaveOutcome,
  needsSave,
  type OutcomeMessage,
  type SwitchCliAccess,
} from "@/lib/switchApplyResult";
import { ErrorBanner } from "@/components/ui/Page";
import { Button } from "@/components/ui/Button";
import { RoleGate } from "@/components/auth/RoleGate";
import { cn } from "@/lib/cn";

// =========================================================================================
// shared pieces (also used by SwitchPortsSection and routes/SwitchPorts)
// =========================================================================================

/**
 * "Route: Via gateway JH-GW-01 to 192.168.1.32" — where Apply will connect.
 *
 * Shown on every preview because a relayed switch's owgw address is the GATEWAY's WAN IP; an
 * operator who sees only that address cannot tell which box a session will really land on.
 * An unresolved path is a warning (Apply resolves again and refuses if it still cannot).
 */
export function AccessPathLine({ access }: { access: SwitchCliAccess | null | undefined }) {
  const text = describeAccess(access);
  if (!text) return null;
  const unresolved = access?.mode === "unresolved";
  return (
    <p
      className={cn("text-[11px] leading-snug", unresolved ? "text-warn" : "text-ink-muted")}
      title={access?.reason ?? undefined}
    >
      <span className="font-medium text-ink-subtle">Route </span>
      <span className="break-words font-mono">{text}</span>
    </p>
  );
}

/** A result/consequence box on the status ramp. `children` renders under the text (actions). */
export function OutcomeNote({
  message,
  children,
}: {
  message: OutcomeMessage;
  children?: React.ReactNode;
}) {
  return (
    <div
      role={message.tone === "critical" ? "alert" : "status"}
      className={cn(
        "rounded border p-2 text-xs",
        message.tone === "healthy" && "border-healthy bg-healthy-soft text-healthy",
        message.tone === "warn" && "border-warn bg-warn-soft text-warn",
        message.tone === "critical" && "border-critical bg-critical-soft text-critical",
      )}
    >
      <div className="font-medium">{message.title}</div>
      {message.detail && <p className="mt-0.5 break-words">{message.detail}</p>}
      {children}
    </div>
  );
}

/** The "Save to startup-config" checkbox every switch write form carries (default ON). */
export function SaveToStartupCheckbox({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label className="flex min-h-touch items-start gap-2 py-1 text-xs text-ink">
      <input
        type="checkbox"
        className="mt-0.5 h-4 w-4 accent-brand"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>
        Save to startup-config
        <span className="block text-[11px] text-ink-subtle">
          {checked
            ? "The change survives a reboot."
            : "Running-config only: the switch reverts this on its next reboot."}
        </span>
      </span>
    </label>
  );
}

/** The exact commands a preview rendered, with the route above them. */
export function CommandPreview({
  preview,
  title = "These commands will run on the switch",
}: {
  preview: PortConfigResponse;
  title?: string;
}) {
  return (
    <div className="rounded border border-line bg-surface p-2">
      <div className="mb-1 text-xs font-medium text-ink">{title}</div>
      <AccessPathLine access={preview.access} />
      {preview.error ? (
        <p className="mt-1 text-xs text-critical">{preview.error}</p>
      ) : (
        <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-words font-mono text-[11px] leading-relaxed text-ink-muted">
          {preview.commands.join("\n")}
        </pre>
      )}
    </div>
  );
}

/**
 * Save running-config to startup-config: Preview (command + route) -> Save.
 *
 * `compact` is the inline form offered under a result that was not confirmed saved; the full
 * form is the dialog's own section. Two steps either way: a save persists EVERYTHING running on
 * the switch, including a half-applied change, so the operator sees what it does first.
 */
export function SaveRunningConfig({ serial, compact = false }: { serial: string; compact?: boolean }) {
  const save = useSwitchSave(serial);
  const [preview, setPreview] = useState<PortConfigResponse | null>(null);
  const [result, setResult] = useState<PortConfigResponse | null>(null);

  const doPreview = () => {
    setResult(null);
    save.mutate({ dry_run: true }, { onSuccess: setPreview });
  };
  const doSave = () =>
    save.mutate(
      { dry_run: false },
      {
        onSuccess: (res) => {
          setResult(res);
          setPreview(null);
        },
      },
    );

  return (
    <div className={cn("flex flex-col gap-2", compact && "mt-2")}>
      {!preview && (
        <Button
          variant="secondary"
          className={compact ? "self-start" : undefined}
          disabled={save.isPending}
          onClick={doPreview}
        >
          {save.isPending ? "Rendering…" : compact ? "Save to startup-config…" : "Preview save"}
        </Button>
      )}
      {preview && (
        <>
          <CommandPreview preview={preview} title="This will run on the switch" />
          <div className="flex gap-2">
            <RoleGate min="msp_admin" mode="disable" reason="Saving switch config requires admin.">
              <Button variant="primary" disabled={save.isPending || !!preview.error} onClick={doSave}>
                {save.isPending ? "Saving…" : "Save to startup-config"}
              </Button>
            </RoleGate>
            <Button variant="ghost" onClick={() => setPreview(null)}>
              Cancel
            </Button>
          </div>
        </>
      )}
      {save.error && <ErrorBanner error={save.error} />}
      {result && <OutcomeNote message={describeSaveOutcome(result)} />}
    </div>
  );
}

// =========================================================================================
// the dialog
// =========================================================================================

/** Plain-words consequence of each 802.1X direction, shown before Preview/Apply. */
const DOT1X_CONSEQUENCE: Record<"on" | "off", OutcomeMessage> = {
  on: {
    tone: "warn",
    title: "Turning 802.1X on can cut clients off",
    detail:
      "Every port set to 802.1X Auto starts demanding authentication. Devices on those ports " +
      "have no network until they log in, and LLDP on them can stop too, so neighbours such " +
      "as APs and uplinks may disappear from this page.",
  },
  off: {
    tone: "warn",
    title: "Turning 802.1X off opens every port that was enforcing it",
    detail:
      "Anything plugged into a port that required authentication reaches the network without " +
      "logging in.",
  },
};

export function SwitchSettingsDialog({
  serial,
  switchName,
  onClose,
}: {
  serial: string;
  /** Shown under the title; the serial when the caller has no friendlier name. */
  switchName?: string | null;
  onClose: () => void;
}) {
  const dot1x = useSwitchDot1x(serial);
  // No default choice: Meridian does not read the switch's current 802.1X state yet, so
  // pre-selecting either would be a guess presented as a fact.
  const [choice, setChoice] = useState<"on" | "off" | null>(null);
  const [save, setSave] = useState(true);
  const [preview, setPreview] = useState<SwitchDot1xResponse | null>(null);
  // The inputs the preview was rendered FROM. The preview is only usable while the form still
  // matches it -- any edit makes it stale, so Apply can never send something nobody reviewed.
  const [previewKey, setPreviewKey] = useState<string | null>(null);
  const [result, setResult] = useState<SwitchDot1xResponse | null>(null);

  const formKey = choice ? JSON.stringify({ enabled: choice === "on", save }) : null;
  const fresh = !!preview && preview.dry_run && previewKey !== null && previewKey === formKey;

  const doPreview = () => {
    if (!choice) return;
    const key = formKey;
    setResult(null);
    dot1x.mutate(
      { enabled: choice === "on", save, dry_run: true },
      {
        onSuccess: (res) => {
          setPreview(res);
          setPreviewKey(key);
        },
      },
    );
  };

  /** Apply EXACTLY the previewed request (enabled/save echoed by the backend), never the form. */
  const doApply = () => {
    if (!fresh || !preview) return;
    dot1x.mutate(
      { enabled: preview.enabled, save: preview.save, dry_run: false },
      {
        onSuccess: (res) => {
          setResult(res);
          setPreview(null);
          setPreviewKey(null);
        },
      },
    );
  };

  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-sheet bg-ink/40" />
        <Dialog.Content
          aria-describedby={undefined}
          className="md-dialog fixed left-1/2 top-1/2 z-sheet flex max-h-[90vh] w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 flex-col rounded-lg border border-line bg-overlay shadow-sheet outline-none"
        >
          <div className="flex items-start justify-between gap-3 border-b border-line px-5 py-4">
            <div className="min-w-0">
              <Dialog.Title className="text-sm font-semibold text-ink">Switch settings</Dialog.Title>
              <p className="mt-0.5 truncate font-mono text-xs text-ink-muted">{switchName || serial}</p>
            </div>
            <Dialog.Close
              className="flex h-touch w-touch shrink-0 items-center justify-center rounded text-ink-muted transition-colors hover:bg-surface-2 hover:text-ink"
              aria-label="Close"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </Dialog.Close>
          </div>

          <div className="flex flex-col gap-5 overflow-y-auto px-5 py-4">
            {/* ------------------------------------------------------------- 802.1X --- */}
            <section className="flex flex-col gap-2">
              <div className="flex items-baseline justify-between gap-2">
                <h3 className="text-[0.6875rem] font-semibold uppercase tracking-wide text-ink-subtle">
                  802.1X authentication
                </h3>
                <span className="text-[11px] text-ink-subtle">Whole switch</span>
              </div>
              <p className="text-xs text-ink-muted">
                Decides whether ports set to 802.1X Auto actually require authentication.
                Meridian cannot read the current setting yet — choose the state the switch
                should be in.
              </p>

              <div role="radiogroup" aria-label="802.1X authentication" className="grid grid-cols-2 gap-2">
                {(["on", "off"] as const).map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    role="radio"
                    aria-checked={choice === opt}
                    onClick={() => setChoice(opt)}
                    className={cn(
                      "flex min-h-touch flex-col items-start justify-center rounded border px-3 py-2 text-left transition-colors",
                      choice === opt
                        ? "border-brand bg-brand-soft ring-1 ring-brand"
                        : "border-line bg-surface hover:bg-surface-2",
                    )}
                  >
                    <span className="text-sm font-medium text-ink">{opt === "on" ? "On" : "Off"}</span>
                    <span className="text-[11px] text-ink-muted">
                      {opt === "on" ? "Require authentication" : "No authentication"}
                    </span>
                  </button>
                ))}
              </div>

              {choice && <OutcomeNote message={DOT1X_CONSEQUENCE[choice]} />}

              <SaveToStartupCheckbox checked={save} onChange={setSave} />

              {preview && !fresh && (
                <p className="text-[11px] text-ink-subtle">
                  Settings changed since the preview — preview again before applying.
                </p>
              )}
              {fresh && preview && <CommandPreview preview={preview} />}

              {dot1x.error && <ErrorBanner error={dot1x.error} />}

              <div className="flex flex-wrap gap-2">
                <Button variant="secondary" disabled={!choice || dot1x.isPending} onClick={doPreview}>
                  {dot1x.isPending && !fresh ? "Rendering…" : "Preview"}
                </Button>
                <RoleGate min="msp_admin" mode="disable" reason="Applying switch config requires admin.">
                  <Button
                    variant="primary"
                    disabled={!fresh || dot1x.isPending || !!preview?.error}
                    onClick={doApply}
                  >
                    {dot1x.isPending && fresh
                      ? "Applying…"
                      : fresh && preview
                        ? `Turn 802.1X ${preview.enabled ? "on" : "off"}`
                        : "Apply"}
                  </Button>
                </RoleGate>
              </div>

              {result && (
                <OutcomeNote message={describeApplyOutcome(result, { saveRequested: result.save })}>
                  {needsSave(result) && <SaveRunningConfig serial={serial} compact />}
                </OutcomeNote>
              )}
            </section>

            {/* --------------------------------------------------------------- save --- */}
            <section className="flex flex-col gap-2 border-t border-line pt-4">
              <h3 className="text-[0.6875rem] font-semibold uppercase tracking-wide text-ink-subtle">
                Save running config
              </h3>
              <p className="text-xs text-ink-muted">
                Writes everything currently running on the switch to startup-config so it
                survives a reboot — including changes made outside Meridian. Use it after a
                change that applied but did not save.
              </p>
              <SaveRunningConfig serial={serial} />
            </section>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
